import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileVideo } from "lucide-react";

const STATUS_COLORS = {
  ready: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  processing: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
  recording: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400",
  scheduled: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
};

export default function MeetingsLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ meetings: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [platform, setPlatform] = useState(searchParams.get("platform") || "all");

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (platform !== "all") params.platform = platform;
    try {
      const res = await api.get("/meetings", { params });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [q, status, platform]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (status !== "all") next.status = status;
    if (platform !== "all") next.platform = platform;
    setSearchParams(next, { replace: true });
  }, [q, status, platform, setSearchParams]);

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Library</div>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">Meetings</h1>
          </div>
          <div className="font-mono text-xs uppercase text-muted-foreground">{data.total} total</div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, transcript, attendees…"
              className="pl-9"
              data-testid="meetings-search-input"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full md:w-40" data-testid="meetings-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="recording">Recording</SelectItem>
              <SelectItem value="ready">Ready</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="w-full md:w-40" data-testid="meetings-platform-filter">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              <SelectItem value="zoom">Zoom</SelectItem>
              <SelectItem value="meet">Google Meet</SelectItem>
              <SelectItem value="teams">Microsoft Teams</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
          </div>
        ) : data.meetings.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-16 text-center">
            <FileVideo className="mx-auto h-10 w-10 text-muted-foreground" />
            <h3 className="mt-4 font-display text-xl font-medium">No meetings found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {q || status !== "all" || platform !== "all"
                ? "Try adjusting your filters."
                : "Send a bot to your first meeting from the Home page."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3" data-testid="meetings-list">
            {data.meetings.map((m) => {
              const dt = new Date(m.scheduled_at);
              return (
                <Link
                  key={m.meeting_id}
                  to={`/app/meetings/${m.meeting_id}`}
                  className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 transition-colors hover:bg-accent/30"
                  data-testid={`meeting-card-${m.meeting_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="truncate font-medium group-hover:text-primary">{m.title}</h3>
                      <Badge className={`text-xs capitalize ${STATUS_COLORS[m.status] || ""}`} variant="secondary">
                        {m.status}
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-xs text-muted-foreground">
                      <span>{dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                      <span>·</span>
                      <span>{Math.round((m.duration_seconds || 0) / 60)} MIN</span>
                      <span>·</span>
                      <span className="uppercase">{m.platform}</span>
                      <span>·</span>
                      <span>{m.action_count || 0} ACTIONS</span>
                    </div>
                    {m.tags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.tags.map((t) => (
                          <span key={t} className="rounded-full bg-accent px-2 py-0.5 text-xs">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-4 hidden md:flex -space-x-2">
                    {(m.attendees || []).slice(0, 4).map((a, i) => (
                      <InitialsAvatar key={i} name={a.name} size="sm" className="ring-2 ring-card" />
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
